import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { industryTrainings } from '../data/industries/industryTrainingsData';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AllTrainingsView } from '../components/training/AllTrainingsView';

export const IndustryTrainings = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // SEO configuration
  const SITE_URL = 'https://www.isde.academy';
  const canonicalUrl = `${SITE_URL}/industry-training`;

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Industry Training",
        "item": canonicalUrl
      }
    ]
  };

  // Organization structured data
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "ISDE Academy",
    "url": SITE_URL,
    "description": "Comprehensive industry-specific training programs for Manufacturing, IT, Healthcare, Aerospace, Medical Devices, Corporate, and Logistics sectors.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Industry Training Programs",
      "itemListElement": industryTrainings.map(industry => ({
        "@type": "OfferCatalog",
        "name": `${industry.name} Training`,
        "numberOfItems": industry.trainings.length
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Industry Training Programs | ISDE Academy</title>
        <meta
          name="description"
          content="Comprehensive industry-specific training programs for Manufacturing, IT, Healthcare, Aerospace, Medical Devices, Corporate, and Logistics sectors. Browse all 36+ expert-led certification courses."
        />
        <meta
          name="keywords"
          content="industry training programs india, manufacturing training, IT training, healthcare training, logistics training, professional certification courses, lean six sigma, ISO training"
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content="Industry Training Programs | ISDE Academy" />
        <meta property="og:description" content="Comprehensive industry-specific training programs for Manufacturing, IT, Healthcare, Aerospace, Medical Devices, Corporate, and Logistics sectors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>

        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptLTEyIDEyYzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02eiIgZmlsbD0iI2ZmZiIvPjwvZz48L3N2Zz4=')] opacity-20" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-semibold mb-6"
            >
              INDUSTRY-SPECIFIC TRAINING
            </motion.div>

            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Training Programs Tailored to Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-cyan-300">
                Industry Needs
              </span>
            </h1>

            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              Specialized training solutions designed for Manufacturing, IT, Healthcare, Education,
              Corporate, and Logistics sectors with expert-led programs and globally recognized certifications.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
                onClick={() => document.getElementById('industries').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore by Industry
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
                onClick={() => document.getElementById('all-programs').scrollIntoView({ behavior: 'smooth' })}
              >
                Browse All Programs
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Why Industry-Specific Training */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Industry-Specific Training?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Generic training doesn't address unique industry challenges. Our specialized programs
              deliver targeted knowledge and skills that directly apply to your sector.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Industry-Relevant Content',
                description: 'Training designed around specific industry standards, regulations, and best practices'
              },
              {
                title: 'Expert Instructors',
                description: 'Learn from professionals with real-world experience in your industry'
              },
              {
                title: 'Practical Applications',
                description: 'Case studies and examples directly relevant to your daily work challenges'
              },
              {
                title: 'Recognized Certifications',
                description: 'Earn credentials that are valued and recognized within your industry'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl"
              >
                <CheckCircle className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section id="industries" ref={ref} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choose Your <span className="text-blue-600">Industry</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select your industry to explore specialized training programs designed for your sector's unique requirements
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {industryTrainings.map((industry) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.id}
                  variants={itemVariants}
                  className="group relative h-96 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/industry-training/${industry.slug}`)}
                >
                  {/* Background Image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${industry.heroImage})` }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300" />

                  {/* Content */}
                  <div className="relative h-full p-8 flex flex-col justify-end text-white z-10">
                    <motion.div
                      className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-all duration-300"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <Icon className="h-8 w-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-gray-200 leading-relaxed mb-4">
                      {industry.description}
                    </p>
                    <div className="flex items-center text-sm font-semibold text-blue-300 group-hover:text-blue-200">
                      {industry.trainings.length} Training Programs
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* All Programs Section */}
      <section id="all-programs" className="bg-white">
        <AllTrainingsView />
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need Custom Training Solutions?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Can't find the exact program you need? We design customized training solutions
              tailored to your organization's specific requirements and goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
